import { useCallback, useMemo } from "react";
import { useMemoizedFn } from "ahooks";
import log from "logbeacon";
import { OspToast } from "@ospweb/sdk-ui";
import { copyText } from "utils";
import throttle from "lodash-es/throttle";

import SharePreviewModalCreateAble from "../components/Chat/SharePreviewModal";
import { getReferencePattern } from "../utils/chatUtils/parseChatMessage";
import { useShare } from "./useShare";
import {
  ChatShareModalCreateAble,
  type ShareOption,
} from "@/src/components/ChatShareModal";

interface ChatMessage {
  id?: string;
  messageId?: string;
  chatId?: string;
  role: string;
  content: string;
  cards?: Array<{ type?: string }>;
  templateType?: string;
  deepsearch?: boolean;
  generateAt?: string;
  createdAt?: string;
}

type AppendMessageArgument = {
  inputValue?: string;
  referId?: string;
  customParams?: Record<string, unknown>;
};

interface UseChatPageMessagesParams {
  messages: ChatMessage[];
  messageIds?: Array<string | undefined>;
  appendMessage: (payload: AppendMessageArgument) => Promise<void> | void;
  referId?: string;
  openAutoScroll?: () => void;
  chatId?: string;
}

interface UseChatPageMessagesResult {
  handleShareAll: () => void;
  onSendRelatedValue: (value: string) => void;
  handleMessageShare: (selected: string[], isAll: boolean) => Promise<void>;
}

export const useChatPageMessages = ({
  messages,
  messageIds,
  appendMessage,
  referId,
  openAutoScroll,
  chatId,
}: UseChatPageMessagesParams): UseChatPageMessagesResult => {
  const { getShareUrl, loading } = useShare({ chatId });
  const handleMessageShare = useMemoizedFn(
    async (selected: string[], isAll: boolean) => {
      try {
        const selectedMessages =
          messages?.filter((msg) =>
            selected.includes(msg.messageId ?? msg.id ?? ""),
          ) || [];

        const selectedAssistantMessages = selectedMessages.filter(
          (msg) => msg.role === "assistant",
        );
        const title = selectedMessages.find(
          (msg) => msg.role === "user",
        )?.content;
        const { question: shareTitle } = getReferencePattern(title || "");
        const ratingType = selectedAssistantMessages.find(
          (msg) => msg?.cards?.[0]?.type === "token" || "stock",
        )?.cards?.[0]?.type;

        const hasIframe = selectedAssistantMessages.some((item) =>
          item.content.includes("<iframe"),
        );

        const isShowGenerateImg =
          selectedAssistantMessages.length > 0 && !hasIframe;

        const shareUrl = await getShareUrl({
          selected,
          type: ratingType,
        });

        if (!shareUrl) {
          return;
        }

        const shareOptions: ShareOption[] = [
          {
            id: "x",
            icon: "twitter",
            title: "X",
            onClick: () => {
              const twitterUrl = `https://twitter.com/compose/post?text=${encodeURIComponent(
                shareTitle
                  ? `Asked @EdgenTech" ${shareTitle?.slice(
                      0,
                      50,
                    )}... "because humans were too slow (again):\n\nHere's its answer:\n`
                  : "Asked @EdgenTech，because humans were too slow (again):\n\nHere's its answer:\n",
              )}&url=${encodeURIComponent(shareUrl)}`;
              window.open(twitterUrl, "_blank");
            },
          },
          {
            id: "telegram",
            icon: "Telegram",
            title: "Telegram",
            onClick: () => {
              const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(
                shareUrl,
              )}`;
              window.open(telegramUrl, "_blank");
            },
          },
        ];

        if (isShowGenerateImg) {
          shareOptions.push({
            id: "image",
            icon: "landscape",
            title: "Generate Image",
            onClick: async () => {
              try {
                await SharePreviewModalCreateAble.call({
                  shareUrl,
                });
              } catch (shareError) {
                log.error(
                  "[SharePreviewModalCreateAble]Image generation failed:",
                  shareError,
                );
              }
            },
          });
        }

        await ChatShareModalCreateAble({
          shareOptions,
          shareUrl,
          isAll,
          onCopyLink: async () => {
            const success = await copyText(shareUrl);
            if (success) {
              OspToast.success({
                content: "Link copied successfully",
              });
            } else {
              OspToast.info({
                content: `Copy link here: ${shareUrl}`,
              });
            }
          },
        });
      } catch (shareError) {
        log.error("[文案]Share failed:", shareError);
      }
    },
  );

  const onSendRelatedValue = useCallback(
    (value: string) => {
      appendMessage({
        inputValue: value,
        referId,
      });
      openAutoScroll?.();
    },
    [appendMessage, openAutoScroll, referId],
  );

  const handleShareAll = useMemoizedFn(() => {
    if (loading) {
      return;
    }
    const validMessageIds =
      messageIds?.filter((id): id is string => Boolean(id)) ?? [];

    if (!validMessageIds.length) {
      return;
    }

    void handleMessageShare(validMessageIds, true);
  });

  const throttledOnClick = useMemo(
    () => throttle(handleShareAll, 100, { leading: true, trailing: false }), // 每 100ms 最多执行一次
    [handleShareAll],
  );

  return {
    handleShareAll: throttledOnClick,
    onSendRelatedValue,
    handleMessageShare,
  };
};
